import { motion } from 'motion/react';
import { ArrowRight, Smartphone, Star, Shield, Zap, Globe, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const trustBadges = [
  { icon: Shield, text: '7 días gratis' },
  { icon: Zap, text: 'IA incluida' },
  { icon: Globe, text: 'App + Web' },
];

function FloatingOrb({ className, delay = 0 }: { className: string; delay?: number }) {
  return (
    <motion.div
      animate={{
        y: [0, -20, 0],
        x: [0, 10, 0],
        opacity: [0.4, 0.7, 0.4],
      }}
      transition={{ duration: 5 + delay, repeat: Infinity, ease: 'easeInOut' }}
      className={`absolute rounded-full blur-2xl ${className}`}
    />
  );
}

export function CTA() {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative max-w-5xl mx-auto"
        >
          {/* Background card with gradient */}
          <div
            className="absolute inset-0 rounded-[2.5rem]"
            style={{
              background: 'linear-gradient(-45deg, #a67eec, #8b6de0, #5bb4f2, #5ddba3, #a67eec)',
              backgroundSize: '400% 400%',
              animation: 'gradient-shift 8s ease infinite',
            }}
          />

          {/* Floating orbs inside */}
          <FloatingOrb className="w-40 h-40 bg-white/10 top-10 left-10" delay={0} />
          <FloatingOrb className="w-60 h-60 bg-white/10 bottom-10 right-10" delay={2} />
          <FloatingOrb className="w-32 h-32 bg-white/15 top-1/2 left-1/3" delay={1} />

          {/* Dot pattern overlay */}
          <div className="absolute inset-0 rounded-[2.5rem] bg-[radial-gradient(circle,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[size:24px_24px]" />

          {/* Light gradient overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent rounded-[2.5rem]" />

          {/* Content */}
          <div className="relative px-8 py-16 md:px-16 md:py-20">
            <div className="text-center">
              {/* Rating */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/20 backdrop-blur-md mb-8 border border-white/20"
              >
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                  ))}
                </div>
                <span className="text-white font-bold">4.9/5</span>
                <span className="text-white/80 text-sm">en Google Play</span>
              </motion.div>

              <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                Empieza hoy con{' '}
                <span className="relative inline-block">
                  <span className="relative z-10">7 días gratis</span>
                  <motion.span
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="absolute -bottom-1 left-0 right-0 h-3 bg-yellow-300/30 rounded-full origin-left"
                  />
                </span>
              </h2>

              <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10">
                Únete a más de <span className="font-bold text-white">50,000 estudiantes</span> en Latinoamérica que ya están mejorando sus calificaciones con Frappi.
              </p>

              {/* Price highlight */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-5 px-7 py-4 rounded-2xl bg-white/15 backdrop-blur-md mb-12 border border-white/20"
              >
                <div className="text-left">
                  <p className="text-white/70 text-sm font-medium">Plan Premium</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-white">$2.99</span>
                    <span className="text-white/70">/mes</span>
                  </div>
                </div>
                <div className="w-px h-14 bg-white/20" />
                <div className="text-left">
                  <p className="text-white/70 text-sm font-medium">Plan Anual</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-extrabold text-white">$24.99</span>
                    <span className="px-2.5 py-1 rounded-full bg-yellow-400 text-yellow-900 text-xs font-bold shadow-lg">
                      -30%
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-[#a67eec] hover:bg-white/95 shadow-2xl shadow-black/20 group h-14 px-8 text-base font-bold"
                  >
                    <Smartphone size={20} className="mr-2" />
                    Descargar en Google Play
                    <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto border-2 border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm h-14 px-8 text-base font-semibold"
                  >
                    Ver demo
                  </Button>
                </motion.div>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center justify-center gap-6 mb-8">
                {trustBadges.map((badge) => (
                  <motion.div
                    key={badge.text}
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center gap-2 text-white/90"
                  >
                    <div className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <badge.icon size={14} />
                    </div>
                    <span className="text-sm font-semibold">{badge.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Bottom assurance */}
              <div className="pt-6 border-t border-white/15">
                <div className="flex flex-wrap items-center justify-center gap-5 text-white/70 text-sm">
                  {['Sin tarjeta requerida', 'Cancela cuando quieras', 'Pago seguro via Google Play'].map((text) => (
                    <span key={text} className="flex items-center gap-1.5">
                      <Check size={14} className="text-white/60" />
                      {text}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
